import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, MailPlus, BellRing, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputCls = (err) =>
  `bg-transparent border-b ${err ? "border-red-700" : "border-[#E5E5E5]"} focus:border-[#B69A58] outline-none py-3 px-0 w-full text-base transition-colors duration-300 rounded-none placeholder-[#A3A3A3]`;

const labelCls = "block text-xs uppercase tracking-widest text-[#737373] font-medium mb-1";

const EmailForm = ({ endpoint, kind, buttonLabel, successText, testPrefix, privacyTestId }) => {
  const openedAt = useRef(Date.now());
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | demo

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!EMAIL_RE.test(email.trim())) {
      setError("Zadejte prosím platnou e-mailovou adresu.");
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch(`${API}/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, website, elapsed_ms: Date.now() - openedAt.current, ...(kind ? { kind } : {}) }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.status === "ok") {
        setStatus("sent");
      } else if (res.ok && data.status === "demo") {
        setStatus("demo");
      } else {
        setStatus("idle");
        setError(typeof data.detail === "string" ? data.detail : "Nepodařilo se odeslat. Zkuste to prosím znovu.");
      }
    } catch {
      setStatus("idle");
      setError("Nepodařilo se odeslat. Zkontrolujte prosím připojení a zkuste to znovu.");
    }
  };

  if (status === "sent") {
    return (
      <div role="status" data-testid={`${testPrefix}-success`} className="flex items-center gap-4 mt-6 border border-[#F0F0F0] p-5">
        <Check className="h-5 w-5 text-[#B69A58] shrink-0" strokeWidth={1.5} aria-hidden="true" />
        <p className="text-sm text-[#252525]">{successText}</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate data-testid={`${testPrefix}-form`} className="mt-6">
      <label htmlFor={`${testPrefix}-email`} className={labelCls}>
        Váš e-mail *
      </label>
      <input
        id={`${testPrefix}-email`}
        data-testid={`${testPrefix}-email-input`}
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => { setEmail(e.target.value); setError(""); }}
        className={inputCls(error)}
        aria-invalid={!!error}
      />
      {error && <p role="alert" data-testid={`${testPrefix}-error`} className="text-sm text-red-700 mt-2">{error}</p>}
      {status === "demo" && (
        <p role="status" data-testid={`${testPrefix}-demo`} className="text-sm text-[#737373] border border-[#E5E5E5] p-4 mt-3">
          Demonstrační režim: uloženo, ale e-mailové doručování zatím není nastavené.
        </p>
      )}
      <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
        <label htmlFor={`${testPrefix}-website`}>Webové stránky</label>
        <input
          id={`${testPrefix}-website`}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>
      <button
        type="submit"
        data-testid={`${testPrefix}-submit`}
        disabled={status === "sending"}
        className="mt-6 w-full border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-6 py-4 text-xs uppercase tracking-widest font-medium disabled:opacity-50"
      >
        {status === "sending" ? "Odesílám…" : buttonLabel}
      </button>
      <p className="text-xs text-[#737373] leading-relaxed mt-4">
        <Link to="/ochrana-osobnich-udaju" data-testid={privacyTestId} className="underline underline-offset-2 hover:text-[#B69A58] transition-colors duration-300">
          Informace o zpracování osobních údajů
        </Link>
      </p>
    </form>
  );
};

const ReminderForm = () => {
  const openedAt = useRef(Date.now());
  const [form, setForm] = useState({ name: "", email: "", website: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    const next = {};
    if (form.name.trim().length < 2) next.name = "Napište prosím své jméno.";
    if (!EMAIL_RE.test(form.email.trim())) next.email = "Zadejte prosím platnou e-mailovou adresu.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setStatus("sending");
    try {
      const res = await fetch(`${API}/reminder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, elapsed_ms: Date.now() - openedAt.current }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.status === "ok") {
        setStatus("sent");
      } else if (res.ok && data.status === "demo") {
        setStatus("demo");
      } else {
        setStatus("idle");
        setServerError(typeof data.detail === "string" ? data.detail : "Připomínku se nepodařilo nastavit. Zkuste to prosím znovu.");
      }
    } catch {
      setStatus("idle");
      setServerError("Připomínku se nepodařilo nastavit. Zkontrolujte prosím připojení a zkuste to znovu.");
    }
  };

  if (status === "sent") {
    return (
      <div role="status" data-testid="reminder-success" className="flex items-center gap-4 mt-6 border border-[#F0F0F0] p-5">
        <Check className="h-5 w-5 text-[#B69A58] shrink-0" strokeWidth={1.5} aria-hidden="true" />
        <p className="text-sm text-[#252525]">Hotovo — připomínka je nastavená. Potvrzení najdete ve své e-mailové schránce.</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate data-testid="reminder-form" className="mt-6 space-y-6">
      <div>
        <label htmlFor="reminder-name" className={labelCls}>Jméno *</label>
        <input
          id="reminder-name"
          data-testid="reminder-name-input"
          type="text"
          autoComplete="name"
          value={form.name}
          onChange={set("name")}
          className={inputCls(errors.name)}
          aria-invalid={!!errors.name}
        />
        {errors.name && <p role="alert" data-testid="reminder-name-error" className="text-sm text-red-700 mt-2">{errors.name}</p>}
      </div>
      <div>
        <label htmlFor="reminder-email" className={labelCls}>E-mail *</label>
        <input
          id="reminder-email"
          data-testid="reminder-email-input"
          type="email"
          autoComplete="email"
          value={form.email}
          onChange={set("email")}
          className={inputCls(errors.email)}
          aria-invalid={!!errors.email}
        />
        {errors.email && <p role="alert" data-testid="reminder-email-error" className="text-sm text-red-700 mt-2">{errors.email}</p>}
      </div>

      <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
        <label htmlFor="reminder-website">Webové stránky</label>
        <input
          id="reminder-website"
          data-testid="reminder-honeypot"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={set("website")}
        />
      </div>

      {serverError && <p role="alert" data-testid="reminder-server-error" className="text-sm text-red-700">{serverError}</p>}
      {status === "demo" && (
        <p role="status" data-testid="reminder-demo-notice" className="text-sm text-[#737373] border border-[#E5E5E5] p-4">
          Demonstrační režim: připomínka byla uložena, ale e-mailové doručování zatím není nastavené.
        </p>
      )}
      <button
        type="submit"
        data-testid="reminder-submit-button"
        disabled={status === "sending"}
        className="w-full border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-6 py-4 text-xs uppercase tracking-widest font-medium disabled:opacity-50"
      >
        {status === "sending" ? "Nastavuji…" : "Připomenout za rok"}
      </button>
      <p className="text-xs text-[#737373] leading-relaxed">
        Jméno a e-mail použiju výhradně k zaslání jedné připomínky za rok.{" "}
        <Link to="/ochrana-osobnich-udaju" data-testid="reminder-privacy-link" className="underline underline-offset-2 hover:text-[#B69A58] transition-colors duration-300">
          Informace o zpracování osobních údajů
        </Link>
      </p>
    </form>
  );
};

const TOOLS = [
  {
    key: "checklist",
    icon: FileDown,
    title: "Checklist k hypotéce zdarma",
    text: "PDF „Co si připravit k hypotéce“ — dokumenty pro zaměstnance, OSVČ i firmy, přehledně k odškrtnutí. Pošlu vám ho e-mailem.",
    cta: "Získat checklist",
    dialogTitle: "Checklist k hypotéce",
    dialogText: "Zadejte e-mail a checklist vám hned pošlu do schránky.",
    endpoint: "checklist",
    buttonLabel: "Poslat checklist",
    successText: "Hotovo — checklist je ve vaší e-mailové schránce.",
  },
  {
    key: "checklist-zivotni",
    icon: FileDown,
    title: "Checklist k životnímu pojištění",
    text: "PDF „Životní pojištění — co prověřit, než smlouvu podepíšete“: výše krytí, výluky, čekací lhůty i revize. Přehledně k odškrtnutí.",
    cta: "Získat checklist",
    dialogTitle: "Checklist k životnímu pojištění",
    dialogText: "Zadejte e-mail a checklist vám hned pošlu do schránky.",
    endpoint: "checklist",
    kind: "zivotni",
    buttonLabel: "Poslat checklist",
    successText: "Hotovo — checklist je ve vaší e-mailové schránce.",
  },
  {
    key: "newsletter",
    icon: MailPlus,
    title: "Měsíční rada e-mailem",
    text: "Jednou měsíčně jedna praktická rada k hypotékám, úvěrům, investicím a pojištění. Žádný spam — odhlásit se můžete kdykoli.",
    cta: "Odebírat rady",
    dialogTitle: "Měsíční rady e-mailem",
    dialogText: "Zadejte e-mail a jednou měsíčně vám pošlu jednu praktickou radu.",
    endpoint: "newsletter",
    buttonLabel: "Odebírat rady",
    successText: "Přihlášení je hotové — potvrzení najdete ve schránce.",
  },
  {
    key: "reminder",
    icon: BellRing,
    title: "Připomínka kontroly smluv",
    text: "Pojištění se má kontrolovat po každé životní změně — a na to se snadno zapomene. Za rok vám napíšu jedinou zprávu s připomenutím.",
    cta: "Nastavit připomínku",
    dialogTitle: "Připomínka kontroly smluv",
    dialogText: "Nechte mi jméno a e-mail — za rok vám připomenu kontrolu vašich pojistných smluv.",
  },
];

export const ToolsSection = () => {
  return (
    <section id="nastroje" data-testid="tools-section" className="py-24 md:py-32 border-t border-[#F0F0F0] bg-[#FAFAF8]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <Reveal>
          <p className="flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-[#737373] font-medium">
            <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
            Praktické rady a užitečné nástroje
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.15] text-[#252525] mt-6 max-w-2xl">
            Co si můžete nechat poslat
          </h2>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-14">
          {TOOLS.map((tool, i) => {
            const Icon = tool.icon;
            return (
              <Reveal key={tool.key} delay={i * 0.05}>
                <div className="border border-[#F0F0F0] bg-white p-8 md:p-10 h-full flex flex-col" data-testid={`tool-card-${tool.key}`}>
                  <Icon className="h-8 w-8 text-[#B69A58]" strokeWidth={1} aria-hidden="true" />
                  <h3 className="font-display text-2xl text-[#252525] mt-6">{tool.title}</h3>
                  <p className="text-sm md:text-base text-[#4A4A4A] leading-relaxed mt-3 flex-1">{tool.text}</p>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button
                        type="button"
                        data-testid={`tool-open-${tool.key}`}
                        className="mt-8 inline-flex items-center justify-center border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-6 py-3 text-xs uppercase tracking-widest font-medium"
                      >
                        {tool.cta}
                      </button>
                    </DialogTrigger>
                    <DialogContent className="bg-white sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="font-display text-2xl text-[#252525]">{tool.dialogTitle}</DialogTitle>
                        <DialogDescription className="text-sm text-[#4A4A4A]">{tool.dialogText}</DialogDescription>
                      </DialogHeader>
                      {tool.key === "reminder" ? (
                        <ReminderForm />
                      ) : (
                        <EmailForm
                          endpoint={tool.endpoint}
                          kind={tool.kind}
                          buttonLabel={tool.buttonLabel}
                          successText={tool.successText}
                          testPrefix={tool.key}
                          privacyTestId={`${tool.key}-privacy-link`}
                        />
                      )}
                    </DialogContent>
                  </Dialog>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
